import { router, usePage } from "@inertiajs/react"
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { MentionItem } from "@/components/prompt-editor/types"
import { useAuth } from "@/hooks/use-permissions"
import { useTranslation } from "@/hooks/use-translation"
import {
  entityMentionItems,
  mentionEntityWorkspaceHref,
  personMentionItem,
  taskMentionDirectory,
  taskMentionDirectoryKey,
  taskMentionItem,
  type TaskMentionDirectory,
  type TaskMentionTexts,
} from "@/lib/task-mentions"
import { openShotPreview } from "@/lib/open-workspace-agent"
import { openTaskLink } from "@/lib/task-open-link"
import {
  fetchTaskMentionEntities,
  fetchTaskMentionPeople,
  resolveTaskMentionTasks,
  searchTaskMentionTasks,
} from "@/lib/tasks-api"
import type {
  TaskMentionEntity,
  TaskMentionEntityKey,
  TaskMentionPerson,
  TaskMentionTaskLink,
  TaskMentionTaskRef,
  TaskMentions,
} from "@/types/tasks"

export type TaskMentionSearchCopy = {
  minQuery: number
  hint: string
  loading: string
}

export type TaskMentionsValue = {
  directory: TaskMentionDirectory
  people: MentionItem[]
  entities: MentionItem[]
  searchTasks: ((query: string, signal: AbortSignal) => Promise<MentionItem[]>) | null
  ensureTasks: (ids: number[]) => void
  openPerson: ((userId: number) => void) | null
  openTask: (ref: TaskMentionTaskLink) => void
  openEntity: ((ref: TaskMentionEntityKey) => void) | null
  /** Заметки подменяют текст «ищем задачу». У задач поля нет — остаются строки задач и порог 3. */
  mentionSearch?: TaskMentionSearchCopy | null
}

export const TaskMentionsContext = createContext<TaskMentionsValue | null>(null)

const PEOPLE_TTL_MS = 5 * 60 * 1000
const MAX_RESOLVE_IDS = 50
const NO_PEOPLE: TaskMentionPerson[] = []
const NO_ENTITIES: TaskMentionEntity[] = []

type ListCache<T> = Map<string, { at: number; promise: Promise<T[]> }>

const peopleCache: ListCache<TaskMentionPerson> = new Map()
const entitiesCache: ListCache<TaskMentionEntity> = new Map()

// Героев, локации и реквизит заводят на других страницах: после возврата список запрашивается заново.
let mentionPage: string | null = null
router.on("navigate", ({ detail }) => {
  if (mentionPage != null && mentionPage !== detail.page.component) entitiesCache.clear()
  mentionPage = detail.page.component
})

function cachedList<T>(cache: ListCache<T>, key: string, load: () => Promise<T[]>): Promise<T[]> {
  const cached = cache.get(key)
  if (cached && Date.now() - cached.at < PEOPLE_TTL_MS) return cached.promise
  const promise = load()
  cache.set(key, { at: Date.now(), promise })
  promise.catch(() => {
    if (cache.get(key)?.promise === promise) cache.delete(key)
  })
  return promise
}

const loadPeople = (projectId: number) => fetchTaskMentionPeople(projectId).then(({ people }) => people)
const loadEntities = (projectId: number) => fetchTaskMentionEntities(projectId).then(({ entities }) => entities ?? [])

function useProjectMentionList<T>(
  cache: ListCache<T>,
  load: (projectId: number) => Promise<T[]>,
  projectId: number | null,
): T[] | null {
  const auth = useAuth()
  const companyId = auth?.company?.id ?? null
  const userId = auth?.user?.id ?? null
  const key = projectId == null || companyId == null || userId == null ? null : `${companyId}:${userId}:${projectId}`
  const [loaded, setLoaded] = useState<{ key: string; rows: T[] } | null>(null)

  useEffect(() => {
    if (key == null || projectId == null) return
    let active = true
    cachedList(cache, key, () => load(projectId))
      .then((rows) => {
        if (active) setLoaded({ key, rows })
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [cache, load, key, projectId])

  return loaded != null && loaded.key === key ? loaded.rows : null
}

export function useTaskMentionPeople(projectId: number | null): TaskMentionPerson[] | null {
  return useProjectMentionList(peopleCache, loadPeople, projectId)
}

// Герои, локации и реквизит проекта с подуровнями — меню после «@»; null, пока список не загружен.
export function useTaskMentionEntities(projectId: number | null): TaskMentionEntity[] | null {
  return useProjectMentionList(entitiesCache, loadEntities, projectId)
}

export function useTaskMentionTexts(): TaskMentionTexts {
  const { t } = useTranslation("tasks")
  return useMemo(
    () => ({
      unnamed: t("unnamed"),
      taskNoAccess: t("mentions.taskNoAccess"),
      taskDeleted: t("mentions.taskDeleted"),
      taskSearchHint: t("mentions.taskSearchHint"),
      taskSearching: t("mentions.taskSearching"),
      openPerson: t("mentions.openPerson"),
      openTask: t("mentions.openTask"),
      entityDeleted: {
        hero: t("mentions.heroDeleted"),
        loc: t("mentions.locDeleted"),
        prop: t("mentions.propDeleted"),
        shot: t("mentions.shotDeleted"),
        script: t("mentions.scriptDeleted"),
      },
      entitySubDeleted: {
        hero: t("mentions.outfitDeleted"),
        loc: t("mentions.variantDeleted"),
        prop: t("mentions.stateDeleted"),
        shot: t("mentions.shotDeleted"),
        script: t("mentions.scriptDeleted"),
      },
      openEntity: {
        hero: t("mentions.openHero"),
        loc: t("mentions.openLoc"),
        prop: t("mentions.openProp"),
        shot: t("mentions.openShot"),
        script: t("mentions.openScript"),
      },
    }),
    [t],
  )
}

function taskLink(row: TaskMentionTaskLink): TaskMentionTaskLink {
  const { id, state, key, title, project_id: projectId, company_slug: companySlug } = row
  return { id, state, key, title, project_id: projectId, company_slug: companySlug }
}

function withTaskRefs(
  prev: ReadonlyMap<number, TaskMentionTaskRef>,
  refs: TaskMentionTaskRef[],
): ReadonlyMap<number, TaskMentionTaskRef> {
  if (refs.length === 0) return prev
  const next = new Map(prev)
  for (const ref of refs) next.set(ref.id, ref)
  return next
}

type TaskMentionsOptions = {
  lists: TaskMentions[]
  people: TaskMentionPerson[] | null
  entities: TaskMentionEntity[] | null
  search: { excludeTaskId: number | null; projectId: number | null } | null
  teamProjectId: number | null
  entityProjectId: number | null
}

export function useTaskMentionsValue({
  lists,
  people,
  entities,
  search,
  teamProjectId,
  entityProjectId,
}: TaskMentionsOptions): TaskMentionsValue {
  const texts = useTaskMentionTexts()
  const { url } = usePage()
  const [extra, setExtra] = useState<ReadonlyMap<number, TaskMentionTaskRef>>(() => new Map())
  const seenRef = useRef(new Map<number, TaskMentionTaskLink>())
  const requestedRef = useRef(new Set<number>())
  const candidates = people ?? NO_PEOPLE
  const projectEntities = entities ?? NO_ENTITIES
  const fresh: TaskMentionDirectory = {
    ...taskMentionDirectory({ people: candidates, tasks: [...extra.values()], entities: projectEntities }, ...lists),
    entitiesLoaded: entities != null,
  }
  const directoryKey = taskMentionDirectoryKey(fresh)
  const cachedRef = useRef<{ key: string; directory: TaskMentionDirectory } | null>(null)
  if (cachedRef.current?.key !== directoryKey) cachedRef.current = { key: directoryKey, directory: fresh }
  const directory = cachedRef.current.directory
  const directoryRef = useRef(directory)
  directoryRef.current = directory

  const menuPeople = useMemo(
    () => candidates.map((person) => personMentionItem(person, texts.unnamed)),
    [candidates, texts.unnamed],
  )

  const menuEntities = useMemo(() => entityMentionItems(projectEntities), [projectEntities])

  const searchEnabled = search != null
  const excludeTaskId = search?.excludeTaskId ?? null
  const searchProjectId = search?.projectId ?? null
  const searchTasks = useMemo(
    () =>
      searchEnabled
        ? (q: string, signal: AbortSignal) =>
            searchTaskMentionTasks({ q, excludeTaskId, projectId: searchProjectId }, signal).then(({ tasks }) => {
              for (const row of tasks) seenRef.current.set(row.id, taskLink(row))
              return tasks.map(taskMentionItem)
            })
        : null,
    [searchEnabled, excludeTaskId, searchProjectId],
  )

  const ensureTasks = useCallback((ids: number[]) => {
    const known = directoryRef.current.tasks
    const missing = ids.filter(
      (id) => id > 0 && Number.isSafeInteger(id) && !known.has(id) && !requestedRef.current.has(id),
    )
    if (missing.length === 0) return
    const seen = missing.flatMap((id) => seenRef.current.get(id) ?? [])
    if (seen.length > 0) setExtra((prev) => withTaskRefs(prev, seen))
    const rest = missing.filter((id) => !seenRef.current.has(id)).slice(0, MAX_RESOLVE_IDS)
    if (rest.length === 0) return
    for (const id of rest) requestedRef.current.add(id)
    resolveTaskMentionTasks(rest)
      .then(({ tasks }) => setExtra((prev) => withTaskRefs(prev, tasks)))
      .catch(() => {
        for (const id of rest) requestedRef.current.delete(id)
      })
  }, [])

  const openPerson = useMemo(
    () =>
      teamProjectId != null
        ? (userId: number) => router.visit(`/projects/${teamProjectId}/workspace/team/${userId}`)
        : null,
    [teamProjectId],
  )

  const openTask = useCallback((ref: TaskMentionTaskLink) => openTaskLink(url, ref), [url])

  const openEntity = useMemo(
    () =>
      entityProjectId != null
        ? (ref: TaskMentionEntityKey) =>
            // Шот открывается панелью поверх страницы: раздела «шот» со ссылкой нет.
            ref.kind === "shot"
              ? openShotPreview(entityProjectId, ref.id)
              : router.visit(mentionEntityWorkspaceHref(entityProjectId, ref.kind, ref.id, ref.sub_id))
        : null,
    [entityProjectId],
  )

  return useMemo(
    () => ({
      directory,
      people: menuPeople,
      entities: menuEntities,
      searchTasks,
      ensureTasks,
      openPerson,
      openTask,
      openEntity,
    }),
    [directory, menuPeople, menuEntities, searchTasks, ensureTasks, openPerson, openTask, openEntity],
  )
}
